import { readDcAuthFromStorage } from './auth';
import { useAuthStore } from '../store/authStore';

type StoredAuth = {
  token: string | null;
  tenant: string | null;
  appId: string | null;
};

export type EventSourceAuthSnapshot = StoredAuth & {
  hydrated: boolean;
};

function tryParseJson(raw: string | null) {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function nonEmpty(value: unknown): string | null {
  if (value == null) return null;
  const text = String(value).trim();
  return text ? text : null;
}

function readPersistedAuth(): StoredAuth {
  if (typeof window === 'undefined') {
    return { token: null, tenant: null, appId: null };
  }

  const parsed = tryParseJson(localStorage.getItem('auth-storage'));
  const state = parsed?.state || parsed;
  return {
    token: nonEmpty(state?.token),
    tenant: nonEmpty(state?.selectedTenant || state?.tenant || state?.tenantId),
    appId: nonEmpty(state?.app_id || state?.appId),
  };
}

export function getEventSourceAuthSnapshot(): EventSourceAuthSnapshot {
  const store = useAuthStore.getState();
  const dcAuth = readDcAuthFromStorage();
  const persisted = readPersistedAuth();
  const storedAppId = typeof window !== 'undefined' ? nonEmpty(localStorage.getItem('dc.app_id')) : null;
  const storeToken = nonEmpty(store.token);
  const storeTenant = nonEmpty(store.selectedTenant);

  // dc.auth is the canonical session store in prod (raw JWT or JSON wrapper).
  return {
    hydrated: Boolean(store.hydrated),
    token: dcAuth.token || storeToken || persisted.token,
    tenant:
      dcAuth.tenant ||
      (storeToken ? storeTenant : null) ||
      persisted.tenant ||
      import.meta.env.VITE_TENANT_DEFAULT ||
      'dev',
    appId: dcAuth.appId || storedAppId || persisted.appId,
  };
}

export function buildEventSourcePath(
  path: string,
  extraParams: Record<string, unknown> = {},
  snapshot: EventSourceAuthSnapshot = getEventSourceAuthSnapshot()
): string | null {
  if (!snapshot.token) return null;

  const params = new URLSearchParams();
  params.set('token', snapshot.token);

  if (snapshot.tenant) {
    params.set('tenant', snapshot.tenant);
  }

  if (snapshot.appId) {
    params.set('app_id', snapshot.appId);
  }

  for (const [key, value] of Object.entries(extraParams)) {
    if (value == null) continue;
    const text = String(value).trim();
    if (!text) continue;
    params.set(key, text);
  }

  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}${params.toString()}`;
}
